import { useEffect, useRef, useState } from 'react'
import { useDrop } from 'react-dnd'
import { useDispatch, useSelector } from 'react-redux'
import { ItemTypes } from './Constants/ItemTypes'
import { insertEditorElement, moveEditorElement, updateTitle, updateWorkflow } from '../../store/FormCreation/globalSlice'
import DraggableTool from './DraggableTool'
import FormElement from './FormElement'
import Preview from './Preview'
import { TextField, Button, FormControl, InputLabel, Select, MenuItem} from '@mui/material'
import axios from 'axios'
import { enqueueSnackbar } from 'notistack'

const Editor = () => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { title, editorElements, workflow } = useSelector((state) => state.global)
  const dispatch = useDispatch()
  const [workflows, setWorkflows] = useState([]);

  useEffect(() => {
    const fetchWorkflows = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/workflows/all`);
        setWorkflows(response.data.workflows);
      } catch (err) {
        console.error('Failed to fetch workflows:', err);
      }
    };

    fetchWorkflows();
  }, []);

  const ref = useRef(null)

  const [, drop] = useDrop(() => ({
    accept: ItemTypes.BOX,
    drop: (item) => {
      if (!item.index && item.index !== 0 && editorElements.length < 1) {
        dispatch(
          insertEditorElement({
            type: item.type,
          })
        )
      }
    },
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      }
    },
  }))

  drop(ref)

  const handleTitleChange = (e) => {
    dispatch(updateTitle(e.target.value))
  }

  const handleWorkflowChange = (e) => {
    const selectedWorkflowId = e.target.value;
    const selectedWorkflow = workflows.find(wf => wf._id === selectedWorkflowId);
    dispatch(updateWorkflow(selectedWorkflow || {}));
  }

  const renderElements = (element, index) => {
    return (
      <DraggableTool
        key={element.id}
        index={index}
        type={element.type}
        moveElement={(from, to) => dispatch(moveEditorElement({ from, to }))}
      >
        <FormElement withToolkit {...element} />
      </DraggableTool>
    )
  }

  const handleSubmit = async () => {
    try {
      const fields = editorElements.map((element) => ({
        fieldName: element.placeHolder,
        fieldType: element.type,
        options: element.options? element.options : [],
      }))
      const submitData = {
        title, 
        workflow: workflow._id,
        fields
      }
      console.log(submitData);
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/forms/create-form`, submitData);
      enqueueSnackbar('Form created successfully!', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to create form. Please try again.', { variant: 'error' });
    }
  }

  return (
    <>
      {isModalOpen && <Preview onClose={() => setIsModalOpen(false)} />}
      <div className="flex flex-col flex-1 gap-4 w-full min-w-[20rem]">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl">Form Builder</h1>
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            Preview
          </button>
        </div>
        <TextField
            label="Form Title"
            variant="outlined"
            fullWidth
            value={title}
            onChange={handleTitleChange}
        />
        <FormControl fullWidth variant="outlined" className="mt-4">
          <InputLabel id="workflow-select-label">Select Workflow</InputLabel>
          <Select
            labelId="workflow-select-label"
            value={workflow?._id || ''}
            onChange={handleWorkflowChange}
            label="Select Workflow"
          >
            {workflows.map((wf) => (
              <MenuItem key={wf._id} value={wf._id}>
                {wf.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <div
          ref={ref}
          className="min-h-screen w-full bg-gray-200 drop-shadow-lg mt-4 p-7 flex flex-col gap-4 overflow-y-auto max-h-[80vh]"
        >
          {editorElements.length < 1 && (
            <div className="text-center p-10 border-2 border-dashed border-slate-400 rounded-md">
              <h1 className="text-xl text-slate-500">Dropzone</h1>
            </div>
          )}
          {editorElements.map(renderElements)}
        </div>
        {editorElements.length > 0 && (
          <div className="flex justify-center mt-4">
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleSubmit}
            >
              Submit Form
            </Button>
          </div>
        )}
      </div>
    </>
  )
}

export default Editor
